<template>
  <footer class="site-footer">
    <div class="page-section page-section--dark site-footer__main">
      <div class="site-container site-footer__grid">
        <div class="site-footer__brand" v-reveal>
          <router-link class="site-footer__brand-link" :to="pathFor('home')">
            <img :src="company.logo" :alt="company.name" />
            <div>
              <span class="site-footer__eyebrow">{{ company.shortName }}</span>
              <h2>{{ company.name }}</h2>
            </div>
          </router-link>
          <p class="site-footer__description">
            {{ company.description }}
          </p>
          <div class="chip-list">
            <span v-for="item in notes" :key="item">{{ item }}</span>
          </div>
        </div>

        <div class="site-footer__links" v-reveal="{ delay: 80 }">
          <span class="site-footer__eyebrow">{{ labels.quickLinks }}</span>
          <div class="site-footer__quick-list">
            <a
              v-for="item in quickLinks"
              :key="item.href"
              :href="item.href"
              target="_blank"
              rel="noreferrer"
            >
              {{ item.name }}
            </a>
          </div>
        </div>

        <div class="site-footer__contact" v-reveal="{ delay: 140 }">
          <span class="site-footer__eyebrow">{{ labels.contactInfo }}</span>
          <div class="site-footer__contact-list">
            <a class="site-footer__contact-item" :href="company.phoneHref">
              <strong>{{ labels.phone }}</strong>
              <span>{{ company.phone }}</span>
            </a>
            <a class="site-footer__contact-item" :href="company.emailHref">
              <strong>{{ labels.email }}</strong>
              <span>{{ company.email }}</span>
            </a>
            <div class="site-footer__contact-item">
              <strong>{{ labels.address }}</strong>
              <span>{{ company.address }}</span>
            </div>
          </div>
        </div>

        <div class="site-footer__qr surface-card" v-reveal:scale="{ delay: 200 }">
          <img :src="company.qrCode" :alt="company.publicAccountLabel" />
          <strong>{{ company.publicAccountLabel }}</strong>
          <span>{{ labels.qrText }}</span>
        </div>
      </div>
    </div>

    <div class="site-footer__legal">
      <div class="site-container site-footer__legal-inner">
        <span>© 2023 - {{ year }} {{ company.name }}</span>
        <a :href="company.recordUrl" target="_blank" rel="noreferrer">
          {{ company.recordNumber }}
        </a>
      </div>
    </div>
  </footer>
</template>

<script>
import {
  getLangFromPath,
  getRoutePath,
  localizedSite
} from '@/content/localizedSite'

export default {
  name: 'Footer',
  data() {
    return {
      year: new Date().getFullYear()
    }
  },
  computed: {
    lang() {
      return getLangFromPath(this.$route.path)
    },
    site() {
      return localizedSite[this.lang]
    },
    company() {
      return this.site.company
    },
    labels() {
      return this.site.labels
    },
    notes() {
      return this.site.home.hero.notes
    },
    quickLinks() {
      return this.site.quickLinks
    }
  },
  methods: {
    pathFor(key) {
      return getRoutePath(this.lang, key)
    }
  }
}
</script>

<style lang="scss" scoped>
.site-footer__main {
  padding-top: clamp(44px, 7vw, 72px);
  padding-bottom: clamp(44px, 7vw, 72px);
}

.site-footer__grid {
  display: grid;
  grid-template-columns:
    minmax(0, 1.15fr) minmax(160px, 0.6fr) minmax(0, 0.92fr)
    190px;
  gap: 24px;
  align-items: start;
}

.site-footer__brand-link {
  display: flex;
  align-items: center;
  gap: 14px;
}

.site-footer__brand-link img {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: rgba(250, 249, 245, 0.08);
  box-shadow: 0 0 0 1px rgba(176, 174, 165, 0.18);
}

.site-footer__brand-link h2 {
  margin: 4px 0 0;
  font-family: var(--font-serif);
  font-size: 1.38rem;
  font-weight: 500;
  line-height: 1.3;
}

.site-footer__eyebrow {
  display: inline-block;
  color: var(--color-coral);
  font-size: 12px;
  letter-spacing: 0;
}

.site-footer__description {
  margin: 20px 0;
  max-width: 520px;
  color: var(--color-warm-silver);
  line-height: 1.7;
}

.site-footer__quick-list,
.site-footer__contact-list {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}

.site-footer__quick-list a,
.site-footer__contact-item {
  display: block;
  padding: 13px 15px;
  border-radius: 14px;
  background: rgba(250, 249, 245, 0.05);
  box-shadow: 0 0 0 1px rgba(176, 174, 165, 0.12);
}

.site-footer__quick-list a {
  color: var(--color-ivory);
  line-height: 1.5;
}

.site-footer__contact-item strong,
.site-footer__contact-item span {
  display: block;
}

.site-footer__contact-item strong {
  margin-bottom: 5px;
  font-size: 0.94rem;
}

.site-footer__contact-item span {
  color: var(--color-warm-silver);
  line-height: 1.6;
}

.site-footer__qr {
  display: grid;
  gap: 8px;
  justify-items: center;
  padding: 18px;
  text-align: center;
}

.site-footer__qr img {
  width: 132px;
  height: 132px;
}

.site-footer__qr span {
  color: var(--color-stone-gray);
}

.site-footer__legal {
  padding: 16px 0;
  background: #10100f;
  color: var(--color-warm-silver);
}

.site-footer__legal-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
  line-height: 1.6;
}

.site-footer__legal a {
  color: var(--color-ivory);
}

@media (prefers-reduced-motion: no-preference) {
  .site-footer__brand-link img,
  .site-footer__quick-list a,
  .site-footer__contact-item,
  .site-footer__qr {
    transition: transform 0.28s ease, background-color 0.28s ease,
      box-shadow 0.28s ease;
  }

  .site-footer__brand-link:hover img,
  .site-footer__quick-list a:hover,
  .site-footer__contact-item:hover,
  .site-footer__qr:hover {
    transform: translateY(-3px);
  }

  .site-footer__quick-list a:hover,
  .site-footer__contact-item:hover {
    background: rgba(250, 249, 245, 0.08);
  }
}

@media screen and (max-width: 1100px) {
  .site-footer__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .site-footer__qr {
    justify-items: start;
    text-align: left;
  }
}

@media screen and (max-width: 640px) {
  .site-footer__grid {
    grid-template-columns: 1fr;
  }

  .site-footer__legal-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
